"use client";
import { SyntaxHighlighter } from "@/components/syntax-highlighter";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { HoverCard } from "@/components/ui/hover-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { PopoverProps } from "@radix-ui/react-popover";
import {
  Check,
  ChevronsUpDown,
  Copy,
  Expand,
  List,
  ListOrdered,
} from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";

export type SnippetLanguage = string;

export const humanRenderLanguage = (language: SnippetLanguage) => {
  switch (language) {
    case "json":
      return "JSON";
    case "js":
      return "JavaScript";
    case "py":
      return "Python";
    case "sh":
      return "shell";
    case "ts":
      return "TypeScript";
    default:
      return language;
  }
};

interface LanguageSelectorProps
  extends PopoverProps,
    React.ComponentProps<"div"> {
  language: SnippetLanguage;
  languages: SnippetLanguage[];
  onLanguageChange: (language: SnippetLanguage) => void;
}

export const LanguageSelector = ({
  language,
  languages,
  onLanguageChange,
  className,
  ...props
}: LanguageSelectorProps) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="grid gap-2 pl-1">
      <Popover open={open} onOpenChange={setOpen} {...props}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={open}
            aria-label="Select a language"
            className={cn(
              className || "",
              "w-full justify-between aria-expanded:text-accent-foreground font-normal",
            )}
          >
            {language ? humanRenderLanguage(language) : "Select a language..."}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[150px] p-2">
          <HoverCard>
            <Command loop>
              <CommandList className="h-[var(--cmdk-list-height)] max-h-[400px]">
                <CommandEmpty>No Languages found.</CommandEmpty>
                {languages.map((l) => (
                  <CommandItem
                    key={l}
                    onSelect={() => {
                      onLanguageChange(l);
                      setOpen(false);
                    }}
                  >
                    {humanRenderLanguage(l)}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        l === language ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </HoverCard>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export type LanguageExample = {
  language: SnippetLanguage;
  example: string;
};

export type Size = "xs" | "sm";
const showLineNumbersLanguages: SnippetLanguage[] = ["py", "js", "ts"];

const SyntaxHiglighterWrapper = ({
  language,
  size,
  showLineNumbers,
  children,
  headerDivider,
}: {
  language: SnippetLanguage;
  size: Size;
  showLineNumbers: boolean;
  children: string;
  headerDivider: boolean;
}) => {
  return (
    <SyntaxHighlighter
      language={language === "curl" ? "shell" : language}
      size={size}
      showLineNumbers={showLineNumbers}
      customStyle={{
        ...(!headerDivider && {
          paddingTop: "0px",
        }),
      }}
    >
      {children}
    </SyntaxHighlighter>
  );
};
const CodeSnippet = ({
  languageExamples,
  size = "sm",
  className,
  noExpandButton = true,
  noCopyButton,
  noLineNumbersButton = true,
  noPadding = false,
  headerDivider = true,
  headerClassName,
}: {
  title?: string;
  languageExamples: LanguageExample[];
  size?: Size;
  className?: string;
  noExpandButton?: boolean;
  noCopyButton?: boolean;
  noLineNumbersButton?: boolean;
  noPadding?: boolean;
  headerDivider?: boolean;
  headerClassName?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const [showLineNumbers, setShowLineNumbers] = useState(false);
  const [currentLanguageExample, setCurrentLanguageExample] =
    useState<LanguageExample>(languageExamples[0]);
  useEffect(() => {
    languageExamples.length > 0 &&
      setCurrentLanguageExample(
        (pV) =>
          languageExamples.find((lE) => lE.language === pV.language) ||
          languageExamples[0],
      );
  }, [languageExamples]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentLanguageExample.example);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  useEffect(() => {
    const lineNumbers = showLineNumbersLanguages.includes(
      currentLanguageExample?.language,
    );
    setShowLineNumbers(lineNumbers);
  }, [currentLanguageExample]);

  return (
    <div
      className={cn(
        "rounded-md max-h-[400px] border w-full max-w-[780px] overflow-auto lg:max-h-[600px] min-w-[300px] bg-nav isolate",
        !noPadding && "my-3",
        className,
      )}
    >
      <div
        className={cn(
          "text-muted-foreground flex items-center justify-between gap-2 sticky top-0 bg-nav h-[44px] z-10 pr-1",
          headerDivider && "border-b",
          headerClassName,
        )}
      >
        {languageExamples.length > 1 ? (
          <LanguageSelector
            language={currentLanguageExample.language}
            languages={languageExamples.map((lE) => lE.language)}
            onLanguageChange={(language) => {
              setCurrentLanguageExample(
                languageExamples.find((lE) => lE.language === language) ||
                  languageExamples[0],
              );
            }}
          />
        ) : (
          <div className="pl-4 text-sm">
            {humanRenderLanguage(languageExamples[0].language)}
          </div>
        )}
        <div>
          {!noExpandButton && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" title="View full screen">
                  <Expand strokeWidth={1} size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full h-auto max-h-[calc(100vh-20px)] max-w-5xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {humanRenderLanguage(currentLanguageExample.language)}
                  </DialogTitle>
                </DialogHeader>
                <SyntaxHiglighterWrapper
                  language={currentLanguageExample.language}
                  size={size}
                  showLineNumbers={showLineNumbers}
                  headerDivider={true}
                >
                  {currentLanguageExample.example.trim()}
                </SyntaxHiglighterWrapper>
              </DialogContent>
            </Dialog>
          )}
          {!noLineNumbersButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLineNumbers(!showLineNumbers)}
              title={
                showLineNumbers ? "Hide line numbers" : "Show line numbers"
              }
            >
              {showLineNumbers ? (
                <ListOrdered strokeWidth={1} size={16} />
              ) : (
                <List strokeWidth={1} size={16} />
              )}
            </Button>
          )}
          {!noCopyButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copyToClipboard}
              title={copied ? "Copied" : "Copy snippet"}
            >
              {copied ? (
                <Check size={16} />
              ) : (
                <Copy strokeWidth={1} size={16} />
              )}
            </Button>
          )}
        </div>
      </div>
      <div>
        <SyntaxHiglighterWrapper
          language={currentLanguageExample.language}
          size={size}
          showLineNumbers={showLineNumbers}
          headerDivider={headerDivider}
        >
          {currentLanguageExample.example.trim()}
        </SyntaxHiglighterWrapper>
      </div>
    </div>
  );
};

export default CodeSnippet;
